import {useMemo} from 'react'
import {useTranslate} from '@tolgee/react'
import {PatientTherapy} from '@cypro/engine/queries'
import {DoseCalcModeActive} from '@cypro/engine/database/schema'
import {getDoseChangePercent} from '@cypro/engine/utils'
import {checkMedicineOverdoseLevel, formatGfr, getTodayOrNext, timeDiffLabel} from '@/modules/patients/functions'
import {usePatientTherapyContext} from '@/shared/contexts'
import {getDrugDetails} from '@/modules/protocols/functions'
import {extractSteps} from '@/modules/patients-therapies/hooks'
import {useGetSettings} from '@/modules/settings/api'
import {isPatientArrivalEnabled} from '@/modules/settings/functions'
import {multipleDigitsNumberFormatter, numberFormatter} from '@cypro/ui/utils'
import {FlatRow} from '../components/patient-details/therapy/tables/rows'

type GroupedTherapies = {[key: string]: PatientTherapy[] | undefined}

function findAsOfDate<T>(records: readonly T[] | undefined, dateOf: (record: T) => number, targetDate: number) {
  return records?.find(record => dateOf(record) <= targetDate)
}

interface CalcualtionBiometricsParams {
  calculationMethod: DoseCalcModeActive | null | undefined
  therapyDate: number
  characteristicsHistory: readonly {createdAt: number; weight: number | null; bsa: number | null}[]
  gfrHistory: readonly {labTime: number; gfr: number}[]
}

function getCalculationBiometrics({
  calculationMethod,
  therapyDate,
  characteristicsHistory,
  gfrHistory,
}: CalcualtionBiometricsParams) {
  if (calculationMethod === DoseCalcModeActive.enum.BSA) {
    const value = findAsOfDate(characteristicsHistory, c => c.createdAt, therapyDate)?.bsa
    if (value == null) return undefined
    return {label: 'characteristics.bsa', value: numberFormatter.format(value)}
  }
  if (calculationMethod === DoseCalcModeActive.enum.WEIGHT || calculationMethod === DoseCalcModeActive.enum.BSA30) {
    const value = findAsOfDate(characteristicsHistory, c => c.createdAt, therapyDate)?.weight
    if (value == null) return undefined
    return {label: 'characteristics.weight', value: numberFormatter.format(value)}
  }
  if (calculationMethod === DoseCalcModeActive.enum.AUC) {
    const value = findAsOfDate(gfrHistory, g => g.labTime, therapyDate)?.gfr
    if (value == null) return undefined
    return {label: 'characteristics.gfr', value: formatGfr(value)}
  }
  return undefined
}

export function useTherapyFlatRows(): FlatRow[] {
  const {patientDrugAmountHistory, filteredPatientTherapies, characteristicsHistory, gfrHistory, hideNotes} =
    usePatientTherapyContext()

  const groupedTherapies: GroupedTherapies = useMemo(() => {
    return Object.groupBy(filteredPatientTherapies, (therapy: PatientTherapy) => therapy.therapyDate)
  }, [filteredPatientTherapies])

  const todayOrNext = useMemo(() => {
    return getTodayOrNext(Object.keys(groupedTherapies))
  }, [groupedTherapies])

  const {t} = useTranslate('common')
  const {settings} = useGetSettings()
  const patientArrivalEnabled = isPatientArrivalEnabled(settings)
  return useMemo(() => {
    const rows: FlatRow[] = []
    for (const [dateKey, therapiesOnDate] of Object.entries(groupedTherapies)) {
      rows.push({type: 'date-header', dateKey, isCurrent: todayOrNext === Number(dateKey)})

      // offsets are relative to the day's first prescription, so a note before it shows a negative offset;
      // a day with only notes falls back to its first note
      const referenceTherapy = therapiesOnDate?.find(therapy => !therapy.isNote) ?? therapiesOnDate?.[0]

      therapiesOnDate?.forEach(therapy => {
        const timeLabel = referenceTherapy ? timeDiffLabel(referenceTherapy.time, therapy.time) : undefined

        if (therapy.isNote) {
          rows.push({type: 'note', therapy, timeLabel, isCurrent: todayOrNext === Number(dateKey)})
          return
        }

        const therapyStatusItems = extractSteps(therapy, patientArrivalEnabled)
        therapy.medicine.forEach((medicine, medicineIndex) => {
          const hasObservations = Boolean(therapy.observations?.length)
          const overDoseLimit = checkMedicineOverdoseLevel(medicine, patientDrugAmountHistory)
          const doseChangePercent = getDoseChangePercent(medicine.doseProportion)
          const medicationProportion = `
          ${doseChangePercent != null && doseChangePercent > 0 ? t('reduced_by', {percentage: numberFormatter.format(doseChangePercent)}) : ''}`
          const medication = medicine?.prescribedName?.name || medicine?.activeSubstance?.name
          const solution = `${therapy.volumeMode ? `${t(therapy.volumeMode)} ` : ''}${therapy?.carrierSolution?.name || '-'}`
          const solutionVolume = therapy.volume
            ? t('volume_ml', {volume: multipleDigitsNumberFormatter.format(therapy.volume)})
            : ''
          const medicationAmount = `${medicine.totalAmount ? `${multipleDigitsNumberFormatter.format(medicine.totalAmount)} ` : ''} ${getDrugDetails(medicine?.activeSubstance)?.doseUnit?.displayName || '-'} `
          const calculationMethodLabel = medicine.calculationMethod ? t(medicine.calculationMethod) : undefined
          const calculationBiometrics = getCalculationBiometrics({
            calculationMethod: medicine.calculationMethod,
            therapyDate: therapy.therapyDate,
            characteristicsHistory,
            gfrHistory,
          })
          const calculationValueLabel = calculationBiometrics
            ? `${t(calculationBiometrics.label)}: ${calculationBiometrics.value}`
            : undefined
          rows.push({
            type: 'therapy',
            therapy,
            hasObservations,
            medicine,
            medicineIndex,
            timeLabel,
            overdosePreventionLevel: overDoseLimit?.overdosePreventionLevel,
            limit: overDoseLimit?.limit,
            medicationAmount,
            solutionVolume,
            solution,
            medicationProportion,
            medication,
            calculationMethodLabel,
            calculationValueLabel,
            steps: therapyStatusItems,
            isCurrent: todayOrNext === Number(dateKey),
          })
          // Hide notes only drops prescription notes; guideline notes (isNote sequences) always stay
          if (medicine.notes && !hideNotes) {
            rows.push({
              type: 'medicine-note',
              therapyId: therapy.id,
              medicineId: medicine.id,
              notes: medicine.notes,
              isCurrent: todayOrNext === Number(dateKey),
            })
          }
        })

        // the prescription-level note, shown under the last drug like the per-drug notes
        if (therapy.therapyNote && !hideNotes) {
          rows.push({
            type: 'medicine-note',
            therapyId: therapy.id,
            medicineId: therapy.id,
            notes: therapy.therapyNote,
            isCurrent: todayOrNext === Number(dateKey),
          })
        }
      })
    }
    return rows
  }, [
    groupedTherapies,
    todayOrNext,
    patientDrugAmountHistory,
    characteristicsHistory,
    gfrHistory,
    t,
    patientArrivalEnabled,
    hideNotes,
  ])
}
